import { constants as config } from '@config';
import { FontService } from 'src/services/font';
import Store from '@store';

import { getStringsNumberStyle } from '../../../../../src/utils/format';
import { convertUnitsToPixels } from '../../../helpers/convert-units-to-pixels';
import { extractStylesFromMetadata } from './metadata-handler';

class TableOfContentsFactory {
  #canvas;
  #editor;
  #indexOffset;
  #existingPages;
  #fontService;

  constructor () {
    this.#canvas = null;
    this.#editor = null;
    this.#indexOffset = 0;
    this.#existingPages = [];
    this.#fontService = null;
  }

  init () {
    this.#editor = INKIVE.Tool.getInstance().editor;
    this.#canvas = this.#editor.canvas;
    this.#existingPages = Store.getState().metadata?.table_of_contents?.pages?.map?.(page => Number(page?.id) || Number(page)) || [];
    this.#fontService = new FontService();
  }

  async fetchHeadings (offset) {
    this.#indexOffset = offset || 0;
    const pagesElement = await this.#fetchPages();
    const pageNumberStyle = this.#calculatePageNumberStyle(pagesElement);
    const headingsSummary = this.#calculateHeadingsSummary(pagesElement, pageNumberStyle);

    return headingsSummary;
  }

  async fetchTableOfContentsPages () {
    this.#existingPages = Store.getState().metadata?.table_of_contents?.pages?.map?.(page => Number(page?.id ?? page)) || [];
    const pagesElement = await this.#fetchPages();
    const tableOfContentsPages = pagesElement.filter(page => this.#existingPages.includes(Number(page.id)));
    const pagesWithoutTocElements = this.#removeExistedTocElementsFromPage(tableOfContentsPages);

    return pagesWithoutTocElements;
  }

  async fetchTableOfContentsFonts (headingsSummary) {
    const headings = [{ child: headingsSummary }];
    const fonts = {};

    while (headings.length !== 0) {
      const currentHeading = headings.pop();
      const currentFontFamily = currentHeading.font_family;

      if (typeof currentHeading === 'undefined') continue;

      if (currentFontFamily) {
        fonts[currentFontFamily] = [];
      }

      for (let i = currentHeading.child.length - 1; i >= 0; i--) {
        headings.push(currentHeading.child[i]);
      }
    }

    try {
      await this.#fontService.loadFonts(fonts);
    } catch (error) {
      console.warn('Cannot fetch some fonts');
    }
  }

  #removeExistedTocElementsFromPage (tableOfContentsPages) {
    const sanitizedPages = tableOfContentsPages.map(page => {
      const pageElement = page.element;
      if (!pageElement.querySelector('[data-meta="toc"]')) return null;
      Array.from(pageElement.querySelectorAll('[data-meta="toc"]')).forEach(element => element.remove());
      return pageElement;
    });

    return sanitizedPages.filter(Boolean);
  }

  async #fetchPages () {
    this.#existingPages = Store.getState().metadata?.table_of_contents?.pages?.map?.(page => Number(page?.id) || Number(page)) || [];
    const pageList = INKIVE.ProjectFacade.get('designs')
      .map(page => parseInt(page.design_id));

    const pages = await Promise.all(pageList.map(pageId => INKIVE.PageFactory.getPage(pageId)));

    const pagesElement = pages.map(page => {
      const id = page.id;
      const content = page.content;
      const element = id === parseInt(INKIVE.Tool._current_design_id)
        ? this.#createElementFromString(this.#sanitizeContent(this.#editor.getSvgString()))
        : this.#createElementFromString(this.#sanitizeContent(content));
      return { id, element };
    });

    return pagesElement;
  }

  calculateTableOfContentsSize (existingSvgPages = []) {
    const defaultPortraitSize = {
      width: config.tableOfContents.defaultCanvasWidth,
      height: config.tableOfContents.defaultCanvasHeight
    };

    const defaultLandscapeSize = {
      width: config.tableOfContents.defaultCanvasHeight,
      height: config.tableOfContents.defaultCanvasWidth
    };

    if (existingSvgPages.length) {
      const existingWidth = Number(existingSvgPages[0].getAttribute('width'));
      const existingHeight = Number(existingSvgPages[0].getAttribute('height'));
      const originalDimensions = INKIVE.FormatFactory.getCurrentSize();

      if (existingWidth && existingHeight) {
        return {
          width: existingWidth,
          height: existingHeight,
          unit: 'px',
          originalDimensions
        };
      }
    }

    this.#existingPages = Store.getState().metadata?.table_of_contents?.pages?.map?.(page => Number(page?.id) || Number(page)) || [];
    const pageList = INKIVE.ProjectFacade.get('designs')
      .filter(page => !this.#existingPages.includes(parseInt(page.id)))
      .filter(page => !!page.width && !!page.height)
      .sort((a, b) => parseInt(a.page_number) - parseInt(b.page_number));

    if (!pageList.length) {
      return defaultPortraitSize;
    }

    const [firstPage] = pageList;
    const { width, height, unit } = firstPage;
    const [dpi72] = config.dpi.variations;

    const tableWidthPx = convertUnitsToPixels(width, unit, false, dpi72);
    const tableHeightPx = convertUnitsToPixels(height, unit, false, dpi72);

    if (!tableWidthPx || !tableHeightPx) {
      return defaultPortraitSize;
    }

    const isPortraitSize = tableWidthPx <= tableHeightPx;
    const shouldUseDefaultSize = tableWidthPx * tableHeightPx < config.tableOfContents.minimumSize;

    if (shouldUseDefaultSize) {
      return isPortraitSize ? defaultPortraitSize : defaultLandscapeSize;
    }

    return {
      width: tableWidthPx,
      height: tableHeightPx,
      unit: 'px',
      originalDimensions: { width, height, unit }
    };
  }

  #calculatePageNumberStyle (pagesElement) {
    const pagesNumberContent = pagesElement.map(page => {
      const pageNumber = page.element.querySelector('g.textarea[data-meta="page-numbering"]');
      return pageNumber ? pageNumber.textContent.trim() : '';
    });

    return getStringsNumberStyle(pagesNumberContent);
  }

  #calculateHeadingStyle (levelStyles, heading, defaultStyle, i) {
    const levelStyle = levelStyles[heading - 1] || {};

    for (const key in defaultStyle) {
      if (levelStyle[key]) continue;
      levelStyle[key] = defaultStyle[key][i];
    }

    return levelStyle;
  }

  #calculateHeadingsSummary (pagesElement, pageNumberStyle) {
    const contentPagesElement = pagesElement.filter(page => !this.#existingPages.map(Number).includes(Number(page.id)));
    const levelStyles = extractStylesFromMetadata();

    let headingsSummary = [];
    for (let pageIndex = 0; pageIndex < contentPagesElement.length; pageIndex++) {
      const page = contentPagesElement[pageIndex];
      const headingTexts = Array.from(page.element.querySelectorAll('g.textarea[data-heading]'))
        .concat(Array.from(page.element.querySelectorAll('g.textarea:has(text[data-heading])')));
      const pageNumber = page.element.querySelector('g.textarea[data-meta="page-numbering"]');

      const pageNumberContent = pageNumber ? pageNumber.textContent.trim() : '';

      const sortedHeadingTexts = headingTexts.sort((nodeA, nodeB) => {
        const positionA = this.#getTextNodePosition(nodeA);
        const positionB = this.#getTextNodePosition(nodeB);

        return positionA - positionB;
      });

      const headings = sortedHeadingTexts.map(node => {
        const textarea = new INKIVE.Text.Textarea().setDOM(node, false);
        const paragraphs = textarea.text.textContent.split('\n');
        const defaultStyle = {
          font_family: this.#calculateStyles(
            textarea.text.strings,
            textarea.text.font_family,
            'font-family'
          ),
          fill: this.#calculateStyles(
            textarea.text.strings,
            textarea.text.fill,
            'fill'
          )
        };

        const headings = [];
        const prefixes = [];

        for (let i = 0; i < textarea.text.strings.length; i++) {
          const string = textarea.text.strings[i];

          if (string.isEol || i === textarea.text.strings.length - 1) {
            headings.push(Number(string.heading) || Number(textarea.heading) || 0);
          }

          if (i === 0 || (i > 0 && textarea.text.strings[i - 1].isEol)) {
            const tspan = string?.dom?.querySelector('tspan.ordered');
            // TODO: remove this manual blank spaces when we handled spacing of prefix properly in TOC generator
            const blankSpaces = '   ';
            const prefixContent = tspan?.textContent ? tspan?.textContent + blankSpaces : '';
            prefixes.push(prefixContent);
          }
        }

        const result = [];
        for (let i = 0; i < headings.length; i++) {
          const heading = headings[i];
          const title = paragraphs[i];
          const prefix = prefixes[i];
          const content = prefix + title;
          if (!heading || !content) continue;

          const style = this.#calculateHeadingStyle(levelStyles, heading, defaultStyle, i);
          const pageId = page.id;
          const index = pageIndex + this.#indexOffset + 1;
          const child = [];

          result.push({
            title,
            prefix,
            heading,
            page_id: pageId,
            page_number: pageNumberContent,
            page_number_style: pageNumberStyle,
            index,
            child,
            ...style
          });
        }

        return result;
      });

      headingsSummary = headingsSummary.concat(headings.flat());
    }

    return headingsSummary;
  }

  #calculateStyles (strings, defaultStyle, type) {
    const numOfParagraphs = strings.filter(string => string.isEol).length;
    const paragraphStyles = new Array(numOfParagraphs).fill([]);
    let paragraphIndex = 0;
    for (let i = 0; i < strings.length; i++) {
      const string = strings[i];
      const stringStyle = string.styles.filter(style => style.attribute === type);

      paragraphStyles[paragraphIndex] = paragraphStyles[paragraphIndex]?.concat(stringStyle);

      if (string.isEol) paragraphIndex += 1;
    }

    return paragraphStyles.map(style => this.#calculateMainStyle(style, defaultStyle));
  }

  #calculateMainStyle (stringStyle, defaultStyle) {
    if (!stringStyle?.length) return defaultStyle;

    const hashStyleTable = { [defaultStyle]: 1 };
    for (let i = 0; i < stringStyle.length; i++) {
      const weight = Number(stringStyle[i].end - stringStyle[i].start);
      const fontFamily = stringStyle[i].value;

      hashStyleTable[fontFamily] = hashStyleTable[fontFamily] ? hashStyleTable[fontFamily] + weight : weight;
    }

    return Object.keys(hashStyleTable).reduce((a, b) => hashStyleTable[a] > hashStyleTable[b] ? a : b);
  }

  #getTextNodePosition (node) {
    const rect = node.querySelector('rect[y]');
    if (!rect) return 0;

    return parseInt(rect.getAttribute('y')) || 0;
  }

  #createElementFromString (string) {
    const parser = new DOMParser();
    const element = parser.parseFromString(string, 'text/html');
    const svg = element.querySelector('svg');
    svg && svg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
    return svg || element;
  }

  #sanitizeContent (string) {
    let sanitizedString = string;
    sanitizedString = sanitizedString.replace(/<\/tspan>\s*/g, '</tspan>');
    sanitizedString = sanitizedString.replace(/\s*<tspan/g, '<tspan');
    sanitizedString = sanitizedString.replace(/<\/text>\s*/g, '</text>');
    sanitizedString = sanitizedString.replace(/\s*<text/g, '<text');

    return sanitizedString;
  }
}

export default TableOfContentsFactory;
